import { useReactiveVar } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import { clearSessionInvalidated, sessionInvalidatedVar } from 'src/apollo/sessionInvalidation';
import useIsPublicRoute from './useIsPublicRoute';

const SESSION_INVALIDATED_FALLBACK_DELAY_MS = 200;
type SessionInvalidationRedirectState = 'idle' | 'redirecting' | 'locked';

/**
 * When the API invalidates the session, we first try a hard redirect to `/login`.
 * If browser navigation is blocked and the user cancels it, the app is still left
 * running on a private route, so we stop rendering the app and then fall back to
 * a full-screen locked page instead.
 */
export default function useSessionInvalidationRedirect() {
  const isPublicRoute = useIsPublicRoute();
  const isSessionInvalidated = useReactiveVar(sessionInvalidatedVar);
  const redirectAttemptedRef = useRef(false);
  const [redirectState, setRedirectState] = useState<SessionInvalidationRedirectState>('idle');

  useEffect(() => {
    if (!isSessionInvalidated) {
      redirectAttemptedRef.current = false;
      setRedirectState('idle');
      return;
    }

    if (isPublicRoute) {
      redirectAttemptedRef.current = false;
      clearSessionInvalidated();
      setRedirectState('idle');
      return;
    }

    if (redirectAttemptedRef.current) {
      return;
    }

    redirectAttemptedRef.current = true;
    setRedirectState('redirecting');
    const timeoutId = window.setTimeout(() => {
      setRedirectState('locked');
    }, SESSION_INVALIDATED_FALLBACK_DELAY_MS);

    window.location.assign('/login');

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isPublicRoute, isSessionInvalidated]);

  return {
    shouldLockApp: redirectState !== 'idle',
    shouldShowSessionInvalidatedPage: redirectState === 'locked',
  };
}
